import { useEffect, useRef, useState } from 'react';
import type { SessionSnapshot } from '@/game/practiceSession';
import type { SimulatedClip, SimulatedMicrophone } from '@/speech/audio/sources';
import styles from './SimulatedChildPanel.module.css';

interface Manifest {
  clips: SimulatedClip[];
}

const MANIFEST_URL = '/__sim/percept-r/manifest.json';

/** Recordings of `word` with a correct or incorrect /r/. */
function clipsFor(manifest: Manifest | undefined, word: string, correct: boolean) {
  return manifest?.clips.filter((c) => c.word === word && c.correct === correct) ?? [];
}

/**
 * Development only (`?mic=sim`): stands in for a child at the microphone by playing real,
 * clinician-rated recordings of the current word (held-out PERCEPT-R children) into the
 * simulated microphone. "Auto" answers every turn like a child who improves with practice.
 */
export function SimulatedChildPanel({
  mic,
  snapshot,
}: {
  mic: SimulatedMicrophone;
  snapshot: SessionSnapshot;
}) {
  const [manifest, setManifest] = useState<Manifest>();
  const [auto, setAuto] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const busy = useRef(false);
  const attemptsOnWord = useRef(0);

  useEffect(() => {
    void fetch(MANIFEST_URL)
      .then((r) => (r.ok ? (r.json() as Promise<Manifest>) : { clips: [] }))
      .then(setManifest);
  }, []);

  // What the child is asked to say now: the word, or its practice syllable.
  const word = snapshot.target.text;
  useEffect(() => {
    attemptsOnWord.current = 0;
  }, [word]);

  const say = async (correct: boolean) => {
    const options = clipsFor(manifest, word, correct);
    const clip = options[Math.floor(Math.random() * options.length)];
    if (!clip || busy.current) {
      if (!clip)
        setLog((l) =>
          [`no ${correct ? 'correct' : 'incorrect'} recording of “${word}”`, ...l].slice(0, 6),
        );
      return;
    }
    busy.current = true;
    attemptsOnWord.current++;
    setLog((l) =>
      [
        `says “${clip.spoken}” (${correct ? 'correct' : 'incorrect'} /r/; ${clip.source})`,
        ...l,
      ].slice(0, 6),
    );
    try {
      await mic.say(clip.url);
    } finally {
      busy.current = false;
    }
  };

  // Auto: answer each turn after a natural pause; more likely correct after a few tries.
  const phase = snapshot.phase;
  const target = snapshot.target.text;
  const caption = snapshot.caption;
  useEffect(() => {
    if (!auto || phase !== 'listening') return undefined;
    const timer = setTimeout(
      () => {
        const p = Math.min(0.85, 0.3 + 0.15 * attemptsOnWord.current);
        void say(Math.random() < p);
      },
      900 + Math.random() * 800,
    );
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-arm on each new listening turn
  }, [auto, phase, target, caption]);

  const available = manifest?.clips.some((c) => c.word === word) ?? false;
  return (
    <aside className={styles.panel} aria-label="Simulated child (development)">
      <strong>Simulated child</strong>
      <span className={styles.note}>
        {manifest
          ? available
            ? `recordings of “${word}”`
            : `no recordings of “${word}”`
          : 'loading recordings…'}
      </span>
      <div className={styles.buttons}>
        <button type="button" data-testid="sim-correct" onClick={() => void say(true)}>
          Say it right
        </button>
        <button type="button" data-testid="sim-incorrect" onClick={() => void say(false)}>
          Say it wrong
        </button>
        <label>
          <input
            type="checkbox"
            data-testid="sim-auto"
            checked={auto}
            onChange={(e) => {
              setAuto(e.target.checked);
            }}
          />{' '}
          Auto
        </label>
      </div>
      <ol className={styles.log} data-testid="sim-log">
        {log.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ol>
    </aside>
  );
}
